// utils/processMeasurements.js
import { supabase } from '../supabaseClient';
import proj4 from 'proj4';

const FILE_HEADER_TO_KEY_MAP = {
  'proj name': 'ProjName',
  'location': 'Location1',
  'location1': 'Location1',
  'location2': 'Location2',
  'contact': 'Contact',
  'mix name': 'MixName',
  'stone size': 'StoneSize',
  'depth': 'Depth',
  'mtd': 'MTD',
  'offset': 'Offset',
  'station': 'Station',
  'description1': 'Description1',
  'description2': 'Description2',
  'operator': 'Operator',
  'density': 'Density',
  'compaction': 'Compaction',
  'surf temp': 'SurfTemp',
  'gps': 'GPS',
  'gps coord': 'GPS',
  'gps time': 'GPSTime',
  'date/time': 'DateTime',
  'voids': 'Voids',
  'mode': 'Mode',
  'type': 'Type',
};

export async function parsePqidatFile(file) {
  console.groupCollapsed('Step 1: Parsing File');
  console.log(`Starting parsing for file: ${file.name}`);

  const text = await file.text();
  let lines = text.split(/\r?\n/).filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('File is too short. It must contain a header and at least one data row.');
  }

  let gaugeId = null;
  const firstLineIsVersion = lines[0].includes(';') && !lines[0].toLowerCase().includes('proj name') && !lines[0].toLowerCase().includes('density');
  
  if (firstLineIsVersion) {
    const versionLine = lines.shift();
    gaugeId = versionLine.split(';')[1]?.trim() || null;
    console.log(`Detected Version Line. Gauge ID: ${gaugeId}`);
  } else {
    console.log('No Version Line detected. Assuming first line is the header.');
  }

  const headerRow = lines.shift();
  console.log('Identified Header Row:', headerRow);

  const headerText = headerRow.toLowerCase();
  if (!headerText.includes('density') || !headerText.includes('date/time')) {
    console.error('Header validation failed. Missing "density" or "date/time".');
    console.groupEnd();
    throw new Error('Could not find a valid header row.');
  }

  const fileHeaders = headerRow.split(';').map(h => h.trim().toLowerCase());
  const headerColumnCount = fileHeaders.length;
  console.log(`Header has ${headerColumnCount} columns.`);
  
  const indexToKeyMap = fileHeaders.map(header => FILE_HEADER_TO_KEY_MAP[header] || null);

  const records = [];
  let corruptedRowCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const columns = line.split(';');

    if (columns.length !== headerColumnCount) {
      corruptedRowCount++;
      console.warn(`Skipping corrupted row #${i + 1} (column count mismatch). Expected ${headerColumnCount}, got ${columns.length}. Line: "${line}"`);
      continue;
    }

    const record = {};
    indexToKeyMap.forEach((key, index) => {
      if (!key) return;
      let value = columns[index]?.trim() || null;
      if (value === '') value = null;
      if (key === 'GPS' && typeof value === 'string') {
        value = value.replace(/^"(.*)"$/, '$1');
      }
      record[key] = value;
    });
    
    record.gauge_id = gaugeId;
    records.push(record);
  }

  console.log(`Parsing complete. Found ${records.length} valid records.`);
  if (corruptedRowCount > 0) {
    console.warn(`Skipped ${corruptedRowCount} corrupted rows.`);
  }
  console.log('First 3 parsed records:');
  console.table(records.slice(0, 3));
  console.groupEnd();

  return { records, corruptedRowCount };
}

// =================================================================================
// MODIFICATION: UPDATED analyzeFileData FUNCTION
// The logic for 'hasValidData' is now more robust.
// =================================================================================
export const analyzeFileData = (pqidatRecords) => {
  let recordsWithGPS = 0;
  let recordsWithoutGPS = 0;
  let invalidDateTime = 0;

  pqidatRecords.forEach(record => {
    const dbTime = formatLocalToDb(record.DateTime);
    if (!dbTime) {
      invalidDateTime++;
      return; // Skip this record if the date/time is invalid
    }

    // This code only runs for records with a valid date/time
    const rawCoords = record.GPS ? convertGpsToLatLng(record.GPS) : null;
    if (rawCoords) {
      recordsWithGPS++;
    } else {
      recordsWithoutGPS++;
    }
  });

  // THE FIX: Calculate valid records directly
  const validRecordsCount = pqidatRecords.length - invalidDateTime;

  return {
    total: pqidatRecords.length,
    recordsWithGPS,
    recordsWithoutGPS,
    invalidDateTime,
    hasGPSData: recordsWithGPS > 0,
    // THE FIX: Base the validity check on the count of records with good dates
    hasValidData: validRecordsCount > 0,
  };
};

const formatLocalToDb = (rawDate) => {
  if (!rawDate) return null;
  
  const parts = rawDate.split(' ');
  const datePart = parts[0];
  const timePart = parts[1];
  const meridiem = parts.length > 2 ? parts[2].toUpperCase() : null;

  if (!datePart || !timePart) return null;

  const [MMs, DDs, Ys] = datePart.split('/');
  if (!MMs || !DDs || !Ys) return null;
  const MM = Number(MMs), DD = Number(DDs), Y = Number(Ys);
  const year = Y < 100 ? 2000 + Y : Y;

  const [hs, ms, ss = '0'] = timePart.split(':');
  let hh = Number(hs);
  const mm = Number(ms), sec = Number(ss);

  if (meridiem === 'PM' && hh < 12) {
    hh += 12;
  }
  if (meridiem === 'AM' && hh === 12) {
    hh = 0;
  }

  const m2 = String(MM).padStart(2, '0');
  const d2 = String(DD).padStart(2, '0');
  const h2 = String(hh).padStart(2, '0');
  const mi = String(mm).padStart(2, '0');
  const s2 = String(sec).padStart(2, '0');

  return `${year}-${m2}-${d2}T${h2}:${mi}:${s2}`;
};

const normalizeCoords = (coord) => {
  if (!coord) return null;
  const [latStr, lonStr] = coord.split(',').map((s) => s.trim());
  const lat = parseFloat(latStr).toFixed(3);
  const lon = parseFloat(lonStr).toFixed(3);
  return `${lat},${lon}`;
};

const isUtmString = (zoneBand) =>
  /^\d{1,2}[C-HJ-NP-X]$/i.test(zoneBand);

const convertUtmToLatLng = (utmStr) => {
  const [zoneBand, eastingRaw, northingRaw] = utmStr.trim().split(/\s+/);
  const zone = parseInt(zoneBand, 10);
  const band = zoneBand.slice(-1).toUpperCase();
  const south = band < 'N';

  const easting  = parseInt(eastingRaw.replace(/[^\d]/g, ''), 10);
  const northing = parseInt(northingRaw.replace(/[^\d]/g, ''), 10);

  const utmProj = `+proj=utm +zone=${zone} ${south ? '+south' : ''} +datum=WGS84 +units=m +no_defs`;
  const [lon, lat] = proj4(utmProj, 'EPSG:4326', [easting, northing]);
  return `${lat},${lon}`;
};

const convertDmsToLatLng = (gps) => {
  const parts = gps.trim().split(/\s+/);
  if (parts.length !== 6) {
    throw new Error(`Invalid GPS format: ${gps}`);
  }
  const [latDegStr, latMinStr, latDir, lonDegStr, lonMinStr, lonDir] = parts;
  let lat = parseFloat(latDegStr) + parseFloat(latMinStr) / 60;
  let lng = parseFloat(lonDegStr) + parseFloat(lonMinStr) / 60;
  if (latDir.toUpperCase() === 'S') lat = -lat;
  if (lonDir.toUpperCase() === 'W') lng = -lng;
  return `${lat},${lng}`;
};

const convertGpsToLatLng = (gps) => {
  if (!gps) return null;
  const trimmed = gps.trim();

  // NEW: First, check for the simple "lat,lng" format.
  if (trimmed.includes(',')) {
    // It's already in the correct format. We can just clean it up and return.
    return trimmed.split(',').map(s => s.trim()).join(',');
  }

  // Existing logic for UTM and DMS formats
  const firstToken = trimmed.split(/\s+/)[0];
  if (isUtmString(firstToken)) {
    return convertUtmToLatLng(trimmed);
  } else {
    return convertDmsToLatLng(trimmed);
  }
};

export const fetchOrganizationMeasurements = async (userId) => {
  try {
    const { data: orgMember, error: e1 } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .single();
    if (e1 || !orgMember) throw new Error('User not in an organization');

    const { data: members, error: e2 } = await supabase
      .from('organization_members')
      .select('user_id')
      .eq('organization_id', orgMember.organization_id);
    if (e2) throw e2;

    const memberIds = members.map((m) => m.user_id);
    const { data, error: e3 } = await supabase
      .from('measurements')
      .select('GpsTime, GpsCoordinates')
      .in('profile_id', memberIds);
    if (e3) throw e3;

    return data;
  } catch (err) {
    throw new Error(`Failed to fetch organization measurements: ${err.message}`);
  }
};

export const findNewRecords = (pqidatRecords, existingMeasurements) => {
  const existingSet = new Set();
  
  existingMeasurements.forEach((m) => {
    if (m.GpsTime) {
      if (m.GpsCoordinates) {
        existingSet.add(`${m.GpsTime}|${normalizeCoords(m.GpsCoordinates)}`);
      } else {
        existingSet.add(`${m.GpsTime}|NO_GPS`);
      }
    }
  });

  const results = {
    newRecords: [],
    recordsWithGPS: 0,
    recordsWithoutGPS: 0,
    duplicatesFound: 0
  };

  const filteredRecords = pqidatRecords.filter((record) => {
    const dbTime = formatLocalToDb(record.DateTime);
    if (!dbTime) return false;
    
    const rawCoords = record.GPS ? convertGpsToLatLng(record.GPS) : null;
    const coordsNorm = normalizeCoords(rawCoords);
    
    let key;
    if (coordsNorm) {
      key = `${dbTime}|${coordsNorm}`;
      results.recordsWithGPS++;
    } else {
      key = `${dbTime}|NO_GPS`;
      results.recordsWithoutGPS++;
    }
    
    if (existingSet.has(key)) {
      results.duplicatesFound++;
      return false;
    }
    
    return true;
  });

  results.newRecords = filteredRecords;
  return results;
};

export const uploadNewRecords = async (records, userId, projectId = null) => {
  try {
    const toInsert = records.map((r) => {
      const dbTime     = formatLocalToDb(r.DateTime);
      const localIso   = r.DateTime ? new Date(r.DateTime).toISOString() : null;
      const coordsFull = r.GPS ? convertGpsToLatLng(r.GPS) : null;

      return {
        profile_id:             userId,
        projectdescription:     r.ProjName           || null,
        Location1:              r.Location1          || null,
        Location2:              r.Location2          || null,
        Contact:                r.Contact            || null,
        Station:                r.Station            || null,
        Description1:           r.Description1       || null,
        Description2:           r.Description2       || null,
        Operator:               r.Operator           || null,
        Density:                parseFloat(r.Density)    || null,
        Compaction:             parseFloat(r.Compaction) || null,
        SurfaceTemp:            parseFloat(r.SurfTemp)   || null,
        Voids:                  parseFloat(r.Voids)      || null,
        GpsCoordinates:         coordsFull,
        GpsTime:                dbTime,
        LocalTimeOfMeasurement: localIso,
        gauge_type:             r.gauge_id           || null,
        MixName:                r.MixName            || null,
        Mode:                   r.Mode               || null,
        Type:                   r.Type               || null,
      };
    });

    const { data, error } = await supabase
      .from('measurements')
      .insert(toInsert)
      .select();
    if (error) throw error;

    if (projectId && data.length) {
      const mappings = data.map((m) => ({
        measurement_id: m.id,
        project_id:     projectId,
      }));
      const { error: mapErr } = await supabase
        .from('project_mapping')
        .insert(mappings);
      if (mapErr) {
        // rollback on failure
        await supabase
          .from('measurements')
          .delete()
          .in('id', data.map((m) => m.id));
        throw mapErr;
      }
    }

    return data;
  } catch (err) {
    throw new Error(`Failed to upload measurements: ${err.message}`);
  }
};